import { useRevalidateStatusStore } from '@/data/operation/store';
import useOutsideAlerter from '@/utils/onClickOutside';
import { ChevronDown } from '@styled-icons/feather';
import { Button, Text } from '@typedream/ui';
import React, { useContext, useState } from 'react';
import { animated, useTransition } from 'react-spring';
import { TextEditorContext } from '../../sharedComponents/TextEditor/context/TextEditorContext';
import ClientOnlyPortal from '../ClientOnlyPortal';
import LoadingSmall from '../LoadingSmall';
import styles from './PublishButton.module.css';
import PublishSection from './PublishSection';
import PublishedSection from './PublishedSection';

function PublishButton() {
  const [published, setpublished] = useState(false);
  const [open, setopenState] = useState(false);
  const [currentPageOnly, setcurrentPageOnly] = useState(false);
  const { status, resetStatus } = useRevalidateStatusStore();

  const revalidated = status?.endpoints;

  const { save } = useContext(TextEditorContext);

  const onClickTrigger = () => {
    save();
    setpublished(false);
  };

  const publishButtonRef = React.useRef<HTMLButtonElement>(null);
  const publishContentRef = React.useRef<HTMLDivElement>(null);
  const loadingRevalidate = status?.loading;

  const { right: buttonRight, bottom: buttonBottom } =
    publishButtonRef?.current?.getBoundingClientRect() || {
      right: 0,
      bottom: 0,
    };

  const setopen = (o: boolean) => {
    if (!o) {
      resetStatus();
    }
    setopenState(o);
  };

  useOutsideAlerter(publishContentRef, 'publish-button', () => {
    setopen(false);
  });

  const transitions = useTransition(open, {
    from: { opacity: 0, transform: 'translateY(-20px) scale(0.78)' },
    enter: { opacity: 1, transform: 'translateY(0px) scale(1)' },
    leave: { opacity: 0, transform: 'translateY(-20px) scale(0.78)' },
    config: {
      tension: 167,
      friction: 18,
    },
  });

  return (
    <>
      <Button
        ref={publishButtonRef}
        size="xs"
        className="publish-button rounded h-[35px]"
        onClick={() => {
          setopen(!open);
          onClickTrigger();
        }}
        leftIcon={loadingRevalidate ? <LoadingSmall loading /> : undefined}
        rightIcon={<ChevronDown size={16} />}
      >
        {loadingRevalidate ? 'Publishing' : 'Publish'}
      </Button>
      <ClientOnlyPortal>
        {transitions((style, isShowing) =>
          isShowing ? (
            <animated.div
              ref={publishContentRef}
              className="absolute flex flex-col bg-background shadow-md rounded py-2 w-[282px] mt-2"
              style={{
                top: buttonBottom,
                left: buttonRight - 282,
                transformOrigin: 'top right',
                ...style,
              }}
            >
              {published ? (
                <PublishedSection currentPageOnly={currentPageOnly} />
              ) : (
                <PublishSection
                  currentPageOnly={currentPageOnly}
                  setcurrentPageOnly={setcurrentPageOnly}
                  setpublished={setpublished}
                  open={open}
                />
              )}
              {!!revalidated &&
                Object.values(revalidated).filter((v) => v.status === 'failed')?.length > 0 && (
                  <div className={styles.item}>
                    <div
                      className="box-border-only with-lighter-text-color is-size-7 has-fullwidth p-2"
                      style={{ lineHeight: '1.2rem', boxShadow: 'none' }}
                    >
                      <div>
                        <Text size="2xs" variant="secondary">
                          Please wait around 2 minutes for your site to be published to{' '}
                          {Object.entries(revalidated)
                            .reduce((acc, [k, v]) => {
                              if (v.status === 'failed') {
                                return [...acc, k.replace(/https?:\/\//, '')];
                              }

                              return acc;
                            }, [])
                            .join(', ')}
                        </Text>
                      </div>
                    </div>
                  </div>
                )}
            </animated.div>
          ) : null
        )}
      </ClientOnlyPortal>
    </>
  );
}

export default PublishButton;
